import './DocumentSelection.scss';
import {Box, Typography} from '@mui/material';
import {t} from 'i18next';
import ArrRadio from '../../../common/arr-radio/ArrRadio';
import ArrButton from '../../../common/arr-button/ArrButton';
import {ArrToggle} from '../../../common/arr-toggle/ArrToggle';
import ArrTooltipWrapper from '../../../common/arr-tooltip-wrapper/ArrTooltipWrapper';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ArrTreeView from '../../../common/arr-tree-view/ArrTreeView';
import ArrLazyTreeView from '../../../common/arr-tree-view/ArrLazyTreeView';
import ArrModal from '../../../common/arr-modal/ArrModal';
import {TreeViewBaseItem} from '@mui/x-tree-view';
import {ExtendedTreeItemProps} from '../../../../shared/interfaces';
import {
  useEffect,
  useState,
  useRef,
  forwardRef,
  useImperativeHandle,
  useMemo,
  useCallback,
} from 'react';
import {useForm} from 'react-hook-form';
import {zodResolver} from '@hookform/resolvers/zod';
import {useDispatch, useSelector} from 'react-redux';
import {RootState} from '../../../../redux/store';
import {
  documentSelectionSchema,
  DocumentSelectionFormData,
} from '../../../../schemas/productions/productionSchema';
import {
  DocumentSelectionMethod,
  DocumentSelectionApiContextType,
  createDocumentListRequestPayload,
  ToastType,
  FormMode,
  FormAction,
  ProductionStatus,
} from '../CreateNewProductionUtils';
import {
  useGetDocumentSelectionView,
  useSaveDocumentsSelection,
} from '../../../../hooks/productions/useProductions';
import {TreeNodeItem} from '../../../../api/types/production.type';
import {useDocumentList} from '../../../../hooks/document/useDocument';
import {
  storeDocumentListMasterId,
  storeSourceTypeCode,
} from '../../../../redux/slice/docViewSlice';
import {
  setDocListBackNavigation,
  setDocListId,
} from '../../../../redux/slice/docListSlice';
import {SourceTypeCode} from '../../../../shared/constants';
import {clearDocViewState} from '../../../document-viewer/docViewerUtils';
import {useNavigate} from 'react-router-dom';
import {setIsFieldsChanged} from '../../../../redux/slice/productionSlice';

// Type for child component ref
export interface StepChildRef {
  save: () => void;
  next: () => void;
}

type DocumentSelectionProps = {
  productionId: number;
  exportProductionId?: number;
  isProductionSet?: boolean;
  getStatus: (showToast: boolean, status: ToastType, message?: string) => void;
  goNext?: () => void;
  goBack?: () => void;
  mode?: FormMode;
  onStateChange?: (state: {
    saveDisabled: boolean;
    nextDisabled: boolean;
  }) => void;
  tagTreeData?: TreeViewBaseItem<ExtendedTreeItemProps>[];
  skipToDeliveryOptions?: () => void;
  productionStatusCode?: string;
  isUpdateNeeded?: boolean;
  onUpdateNeededChange?: (value: boolean) => void;
};

type TreeItem = TreeViewBaseItem<ExtendedTreeItemProps>;

const transformTreeNodeToTreeView = (
  nodes: TreeNodeItem[]
): TreeViewBaseItem<ExtendedTreeItemProps>[] => {
  if (!nodes || nodes.length === 0) return [];

  const nodeMap = new Map<string, TreeViewBaseItem<ExtendedTreeItemProps>>();
  const rootNodes: TreeViewBaseItem<ExtendedTreeItemProps>[] = [];

  // First pass: create all nodes
  for (let i = 0; i < nodes.length; i++) {
    const node = nodes[i];
    nodeMap.set(node.id, {
      id: node.id,
      label: node.text,
      fileType: node.isGroup ? 'group' : 'item',
      count: '0',
      children: [],
    });
  }

  // Second pass: build hierarchy
  for (let i = 0; i < nodes.length; i++) {
    const node = nodes[i];
    const treeNode = nodeMap.get(node.id);
    if (!treeNode) continue;

    const isRoot =
      !node.parent || node.parent === '#' || !nodeMap.has(node.parent);

    if (isRoot) {
      rootNodes.push(treeNode);
    } else {
      const parent = nodeMap.get(node.parent);
      if (parent?.children) {
        parent.children.push(treeNode);
      } else if (parent) {
        parent.children = [treeNode];
      }
    }
  }

  return rootNodes;
};

const DocumentSelection = forwardRef<StepChildRef, DocumentSelectionProps>(
  (
    {
      productionId,
      exportProductionId = 0,
      isProductionSet = true,
      getStatus,
      goNext,
      mode = FormMode.CREATE,
      onStateChange,
      tagTreeData = [],
      skipToDeliveryOptions,
      productionStatusCode = null,
      isUpdateNeeded = false,
      onUpdateNeededChange,
    },
    ref
  ) => {
    const isInProgressOrCompleted =
      productionStatusCode === ProductionStatus.COMPLETED ||
      productionStatusCode == ProductionStatus.INPROGRESS;

    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [isSaved, setIsSaved] = useState<boolean>(false);

    // Get Redux state
    const selectedSecurityGroupId = useSelector(
      (state: RootState) => state.production.securityGroupId
    );

    // Memoize API params to prevent unnecessary re-renders
    const apiParams = useMemo(
      () => ({
        SecurityGroupId: selectedSecurityGroupId,
        productionId: productionId,
      }),
      [selectedSecurityGroupId, productionId]
    );

    // Memoize enabled condition
    const isApiEnabled = useMemo(
      () => !!selectedSecurityGroupId && productionId > 0,
      [selectedSecurityGroupId, productionId]
    );

    // API hooks
    const {
      data: documentSelectionData,
      isLoading: isDocumentSelectionLoading,
      isFetching: isDocumentSelectionFetching,
      refetch: refetchDocumentSelection,
    } = useGetDocumentSelectionView(apiParams, isApiEnabled);

    const {
      mutate: saveDocumentsSelection,
      isPending: isSavingDocumentsSelection,
    } = useSaveDocumentsSelection(
      (response) => {
        // Store the action before refetch to ensure we don't lose it
        const action = pendingActionRef.current;

        if (action === 'updateDocCount') {
          return;
        }

        // Reset selections from inactive radio buttons
        resetInactiveSelections();

        // Get current selections after reset (only active method)
        // let currentTags = selectedTags;
        // let currentSearches = selectedSearches;
        // let currentFolders = selectedFolders;

        // switch (selectionMethod) {
        //   case DocumentSelectionMethod.TAGS:
        //     currentSearches = [];
        //     currentFolders = [];
        //     break;
        //   case DocumentSelectionMethod.SEARCHES:
        //     currentTags = [];
        //     currentFolders = [];
        //     break;
        //   case DocumentSelectionMethod.FOLDERS:
        //     currentTags = [];
        //     currentSearches = [];
        //     break;
        // }

        // After save, refetch the view
        refetchDocumentSelection({cancelRefetch: false})
          .then((result) => {
            const refreshedSelection =
              result.data?.productionDocumentsSelection;
            const newTotalDocs =
              refreshedSelection?.totalNumberOfDocsIncludingFamily ?? totalDocs;
            const newFamilyDocs =
              refreshedSelection?.totalNumberOfFamilyDocs ?? familyDocs;

            if (newTotalDocs !== totalDocs || newFamilyDocs !== familyDocs) {
              setTotalDocs(newTotalDocs);
              setFamilyDocs(newFamilyDocs);
            }

            const pendingCorrection = pendingSaveCorrectionRef.current;
            if (pendingCorrection) {
              pendingSaveCorrectionRef.current = null;
              persistRealIncludeFamiliesFlag(
                pendingCorrection.realIncludeFamilies,
                pendingCorrection.contextType,
                pendingCorrection.contextIDs,
                newTotalDocs,
                newFamilyDocs
              );
            }
          })
          .catch((error) => {
            console.error('[SAVE] Refetch error', error);
          });
        getStatus(
          true,
          ToastType.Success,
          action === FormAction.NEXT
            ? t(
                'productionsAndExports.createNewProduction.common.nextSuccessToast'
              )
            : response.message ||
                t(
                  'productionsAndExports.createNewProduction.documentSelection.saveSuccess'
                )
        );
        // If action was 'next', navigate to next step
        // This must happen after the save is complete
        if (action === FormAction.NEXT) {
          // Use the memoized shouldSkipPrivGuard value (calculated at component level)
          // This ensures consistency with the skip logic checked in next() handler

          if (!isUpdateNeeded) {
            // If update is not needed, skip immediately
            if (shouldSkipPrivGuard) {
              skipToDeliveryOptions();
            } else {
              goNext?.();
            }
            return;
          }
          // Use setTimeout to ensure navigation happens after state updates
          setTimeout(() => {
            if (shouldSkipPrivGuard) {
              skipToDeliveryOptions();
            } else {
              goNext?.();
              pendingActionRef.current = null;
            }
            pendingActionRef.current = null;
          }, 0);
        } else {
          setIsSaved(true);
          // Update parent state to track that update is needed after save
          onUpdateNeededChange?.(true);
          // Reset form with current values to clear dirty state
          reset({
            action: FormAction.SAVE,
            selectionMethod: selectionMethod,
            selectedTags: selectedTags,
            selectedSearches: selectedSearches,
            selectedFolders: selectedFolders,
            includeFamilies: includeFamilies,
          });
          pendingActionRef.current = null;
        }
      },
      (error) => {
        getStatus(
          true,
          ToastType.Error,
          (error as Error).message ||
            t(
              'productionsAndExports.createNewProduction.documentSelection.validationError'
            )
        );
        pendingActionRef.current = null;
        isUpdatingDocCountRef.current = false;
        pendingSaveCorrectionRef.current = null;
      }
    );

    const {mutate: fetchDocumentList, isPending: isDocListLoading} =
      useDocumentList();

    const pendingActionRef = useRef<FormAction | 'updateDocCount' | null>(null);
    const pendingSaveCorrectionRef = useRef<{
      realIncludeFamilies: boolean;
      contextType: DocumentSelectionApiContextType;
      contextIDs: number[];
    } | null>(null);
    const [selectionMethod, setSelectionMethod] =
      useState<DocumentSelectionMethod>(DocumentSelectionMethod.TAGS);
    const [selectedTags, setSelectedTags] = useState<string[]>([]);
    const [selectedSearches, setSelectedSearches] = useState<string[]>([]);
    const [selectedFolders, setSelectedFolders] = useState<string[]>([]);
    const [indeterminateSearches, setIndeterminateSearches] = useState<
      string[]
    >([]); // Root parent IDs that should show as indeterminate
    const [totalDocs, setTotalDocs] = useState<number>(0);
    const [familyDocs, setFamilyDocs] = useState<number>(0);
    const [includeFamilies, setIncludeFamilies] = useState<boolean>(false);
    const [showExportWarningModal, setShowExportWarningModal] =
      useState<boolean>(false);
    const [componentIsDirty, setComponentIsDirty] = useState<boolean>(false);

    const [lastCountSnapshot, setLastCountSnapshot] = useState<{
      tags: string[];
      searches: string[];
      folders: string[];
    }>({tags: [], searches: [], folders: []});

    const documentListPayload = useMemo(
      () =>
        createDocumentListRequestPayload({
          sourceId: productionId,
          sourceTypeCode: SourceTypeCode.PRODUCTIONTOTAL,
          docViewSourceTypeCode: SourceTypeCode.PRODUCTIONTOTAL,
          sourceInfoOverrides: {
            destinationContextTypeCode: 'DOCLIST',
          },
          docViewSessionOverrides: {
            docsForCodeSame: [''],
          },
        }),
      [productionId]
    );

    const familyDocumentListPayload = useMemo(
      () =>
        createDocumentListRequestPayload({
          sourceId: productionId,
          sourceTypeCode: SourceTypeCode.PRODUCTIONFAMILY,
          docViewSourceTypeCode: SourceTypeCode.PRODUCTIONFAMILY,
          sourceInfoOverrides: {
            destinationContextTypeCode: 'DOCLIST',
          },
          docViewSessionOverrides: {
            docsForCodeSame: [''],
          },
        }),
      [productionId]
    );

    // Keep a copy of the full, fully-expanded tree for lazy-loading source
    const fullTreeRef = useRef<TreeViewBaseItem<ExtendedTreeItemProps>[]>([]);
    // Track if we've already triggered auto-expand clicks for lazy tree (auto-expand disabled - ref kept for potential re-enable)
    // const hasTriggeredAutoExpandRef = useRef(false);
    // Cache for displayedLazyItems to avoid new reference every render (prevents update loop)
    const displayedLazyItemsCacheRef = useRef<{
      source: TreeViewBaseItem<ExtendedTreeItemProps>[];
      disabled: boolean;
      result: TreeViewBaseItem<ExtendedTreeItemProps>[];
    }>({source: [], disabled: false, result: []});

    const countNodes = (nodes: TreeViewBaseItem<ExtendedTreeItemProps>[]) => {
      let count = 0;
      const walk = (arr: TreeViewBaseItem<ExtendedTreeItemProps>[]) => {
        for (const n of arr) {
          count += 1;
          if (Array.isArray(n.children) && n.children.length) walk(n.children);
        }
      };
      walk(nodes);
      return count;
    };

    const findNode = (
      nodes: TreeViewBaseItem<ExtendedTreeItemProps>[],
      id: string
    ): TreeViewBaseItem<ExtendedTreeItemProps> | null => {
      for (const n of nodes) {
        if (n.id === id) return n;
        if (n.children) {
          const f = findNode(n.children, id);
          if (f) return f;
        }
      }
      return null;
    };

    // Helper function to get all child node IDs recursively
    const getAllChildNodeIds = (
      node: TreeViewBaseItem<ExtendedTreeItemProps>
    ): string[] => {
      if (!node.children || node.children.length === 0) return [];
      return node.children.reduce<string[]>((acc, child) => {
        // Skip loading placeholder children
        if (child.id && child.id.includes('-loading')) return acc;
        return [...acc, child.id, ...getAllChildNodeIds(child)];
      }, []);
    };

    const treeData = useMemo(() => {
      let rawData:
        | TreeNodeItem[]
        | TreeViewBaseItem<ExtendedTreeItemProps>[]
        | null = null;

      // Get raw data based on selection method
      if (!documentSelectionData) {
        if (
          selectionMethod === DocumentSelectionMethod.TAGS &&
          tagTreeData?.length
        ) {
          return tagTreeData;
        }
        return [];
      }

      switch (selectionMethod) {
        case DocumentSelectionMethod.TAGS:
          rawData = documentSelectionData.tagsList || null;
          if (!rawData && tagTreeData?.length) {
            return tagTreeData;
          }
          break;
        case DocumentSelectionMethod.SEARCHES:
          rawData = documentSelectionData.searchList || null;
          break;
        case DocumentSelectionMethod.FOLDERS:
          rawData = documentSelectionData.foldersList || null;
          break;
      }

      if (!rawData || rawData.length === 0) {
        return [];
      }

      // Check if already in tree format (has 'label' property)
      const firstItem = rawData[0];
      if (firstItem && 'label' in firstItem && !('text' in firstItem)) {
        const treeData =
          rawData as unknown as TreeViewBaseItem<ExtendedTreeItemProps>[];
        fullTreeRef.current = treeData;
        return treeData;
      }

      // Transform TreeNodeItem[] to TreeView format
      try {
        const transformed = transformTreeNodeToTreeView(
          rawData as unknown as TreeNodeItem[]
        );
        fullTreeRef.current = transformed;
        return transformed;
      } catch (error) {
        console.error('Error transforming tree data:', error);
        return [];
      }
    }, [selectionMethod, documentSelectionData, tagTreeData]);

    // Full SEARCHES tree independent of the currently active `selectionMethod`. The hydration effect below can flip `selectionMethod` to SEARCHES and compute
    const searchesFullTree = useMemo(() => {
      const rawData = documentSelectionData?.searchList;
      if (!rawData || rawData.length === 0) return [];

      const firstItem = rawData[0];
      if (firstItem && 'label' in firstItem && !('text' in firstItem)) {
        return rawData as unknown as TreeViewBaseItem<ExtendedTreeItemProps>[];
      }

      try {
        return transformTreeNodeToTreeView(
          rawData as unknown as TreeNodeItem[]
        );
      } catch (error) {
        console.error('Error transforming searches tree data:', error);
        return [];
      }
    }, [documentSelectionData]);

    // Helper function to get all descendant leaf nodes recursively from a specific node
    const getAllDescendantLeaves = (
      node: TreeViewBaseItem<ExtendedTreeItemProps>
    ): string[] => {
      const result: string[] = [];
      if (node.children && node.children.length > 0) {
        for (const child of node.children) {
          // Skip loading placeholders
          if (child.id && child.id.includes('-loading')) continue;
          // If child has no children, it's a leaf
          if (!child.children || child.children.length === 0) {
            result.push(child.id);
          } else {
            // Recursively get leaves from this child
            const childLeaves = getAllDescendantLeaves(child);
            result.push(...childLeaves);
          }
        }
      }
      return result;
    };

    // Direct children of a node, excluding loading placeholders
    const getDirectChildren = (
      children: TreeViewBaseItem<ExtendedTreeItemProps>[] | undefined
    ): TreeViewBaseItem<ExtendedTreeItemProps>[] =>
      (children || []).filter((c) => c && !c.id?.includes('-loading'));

    // Check if a node has any direct children (leaves or parents) - used to filter selectedItems
    // so parents (including "every child is leaf") are only shown checked when all children are selected.
    const hasDirectChildren = (
      nodes: TreeViewBaseItem<ExtendedTreeItemProps>[],
      nodeId: string
    ): boolean => {
      const node = findNode(nodes, nodeId);
      const directChildren = getDirectChildren(node?.children);
      return directChildren.length > 0;
    };

    // Returns which node IDs should be fully checked vs indeterminate based on their direct children
    // (leaves or nested parents). Used for SEARCHES so parents only show checked when all children are selected.
    // - checked: nodes whose direct children are all selected
    // - indeterminate: nodes with some but not all direct children selected
    const getParentCheckboxState = (
      nodes: TreeViewBaseItem<ExtendedTreeItemProps>[],
      contextIDs: string[]
    ): {checked: string[]; indeterminate: string[]} => {
      const checked: string[] = [];
      const indeterminate: string[] = [];

      const checkNode = (node: TreeViewBaseItem<ExtendedTreeItemProps>) => {
        // Consider any node that has direct children (leaves or nested parents)
        const directChildren = getDirectChildren(node.children);
        if (directChildren.length > 0) {
          let selectedCount = 0;
          const totalCount = directChildren.length;

          for (const child of directChildren) {
            const isLeaf = !child.children || child.children.length === 0;
            if (isLeaf) {
              // Direct leaf: selected only if its ID is in contextIDs
              if (contextIDs.includes(child.id)) selectedCount += 1;
            } else {
              // Direct child is a parent: selected if it has selected descendant or is itself in contextIDs when unloaded
              const descendantLeaves = getAllDescendantLeaves(child);
              const hasSelectedDescendant =
                descendantLeaves.length > 0 &&
                descendantLeaves.some((leafId) => contextIDs.includes(leafId));
              const isParentItselfSelected =
                descendantLeaves.length === 0 && contextIDs.includes(child.id);
              if (hasSelectedDescendant || isParentItselfSelected)
                selectedCount += 1;
            }
          }

          if (selectedCount === totalCount && totalCount > 0) {
            checked.push(node.id);
          } else if (selectedCount > 0 && selectedCount < totalCount) {
            indeterminate.push(node.id);
          }
        }

        // Recursively check children
        for (const child of getDirectChildren(node.children)) {
          checkNode(child);
        }
      };

      // Check all root nodes
      for (const node of nodes) {
        checkNode(node);
      }

      return {checked, indeterminate};
    };

    // Helper function to find all parent node IDs for a given set of selected item IDs
    const findParentNodeIds = (
      nodes: TreeViewBaseItem<ExtendedTreeItemProps>[],
      selectedIds: string[],
      parentIds: string[] = []
    ): string[] => {
      const result: string[] = [];
      for (const node of nodes) {
        if (selectedIds.includes(node.id)) {
          // If this node is selected, add all its parents to the result
          result.push(...parentIds);
        }
        if (node.children && node.children.length > 0) {
          // Recursively check children with this node as a parent
          const childParentIds = [...parentIds, node.id];
          const childResults = findParentNodeIds(
            node.children,
            selectedIds,
            childParentIds
          );
          result.push(...childResults);
        }
      }
      // Remove duplicates
      return [...new Set(result)];
    };

    // For SEARCHES: filter combined selected items so parents appear checked only when fully selected.
    const filterSearchesSelectedItems = (
      combined: string[],
      fullTree: TreeViewBaseItem<ExtendedTreeItemProps>[],
      baseSelected: string[]
    ): string[] => {
      const state = getParentCheckboxState(fullTree, baseSelected);
      return combined.filter(
        (id) => !hasDirectChildren(fullTree, id) || state.checked.includes(id)
      );
    };

    // Get selected items based on selection method
    // For lazy loading, include parent nodes of selected items so they appear checked
    // Always return an array to ensure controlled component behavior
    const selectedItems = useMemo(() => {
      let baseSelected: string[] = [];
      switch (selectionMethod) {
        case DocumentSelectionMethod.TAGS:
          baseSelected = Array.isArray(selectedTags) ? selectedTags : [];
          break;
        case DocumentSelectionMethod.SEARCHES:
          baseSelected = Array.isArray(selectedSearches)
            ? selectedSearches
            : [];
          break;
        case DocumentSelectionMethod.FOLDERS:
          baseSelected = Array.isArray(selectedFolders) ? selectedFolders : [];
          break;
        default:
          baseSelected = [];
      }

      // Ensure baseSelected is always an array (never undefined)
      if (!Array.isArray(baseSelected)) {
        baseSelected = [];
      }

      // For lazy loading, include parent nodes of selected items so they appear checked
      const isLargeData = countNodes(fullTreeRef.current) > 2000;
      if (isLargeData && baseSelected.length > 0 && treeData.length > 0) {
        const parentNodeIds = findParentNodeIds(
          fullTreeRef.current,
          baseSelected
        );
        const combined = [...new Set([...baseSelected, ...parentNodeIds])];

        if (
          selectionMethod === DocumentSelectionMethod.SEARCHES &&
          fullTreeRef.current.length > 0
        ) {
          return filterSearchesSelectedItems(
            combined,
            fullTreeRef.current,
            baseSelected
          );
        }

        return combined;
      }

      return baseSelected;
    }, [
      selectionMethod,
      selectedTags,
      selectedSearches,
      selectedFolders,
      treeData,
    ]);

    // For SEARCHES: compute indeterminate root parent IDs from current selection
    // so that partial child selection shows intermediate state (not fully checked).
    const indeterminateNodeIdsForTree = useMemo(() => {
      if (selectionMethod !== DocumentSelectionMethod.SEARCHES) return [];
      if (!searchesFullTree.length) return indeterminateSearches;
      const baseSelected = Array.isArray(selectedSearches)
        ? selectedSearches
        : [];
      if (baseSelected.length === 0) return [];
      const state = getParentCheckboxState(searchesFullTree, baseSelected);
      return state.indeterminate;
    }, [
      selectionMethod,
      selectedSearches,
      indeterminateSearches,
      searchesFullTree,
    ]);

    // Initialize selections from API response when tree data becomes available (lazy loading case)
    // Only initialize once when tree data first becomes available, not on every treeData change
    useEffect(() => {
      const selection = documentSelectionData?.productionDocumentsSelection;
      if (
        !selection ||
        !selection.contextIDs ||
        selection.contextIDs.length === 0
      )
        return;
      if (isUpdatingDocCountRef.current) return;
      if (isUserClearedRef.current) return;
      if (hasUserInteractedRef.current) return;

      const contextType = selection.contextType || 0;
      const ids = selection.contextIDs.map(String);

      // Check if we need to initialize selections based on API response
      const shouldInitializeSearches =
        contextType === 3 &&
        selectedSearches.length === 0 &&
        ids.length > 0 &&
        selectionMethod === DocumentSelectionMethod.SEARCHES;

      const shouldInitializeTags =
        contextType === 2 &&
        selectedTags.length === 0 &&
        ids.length > 0 &&
        selectionMethod === DocumentSelectionMethod.TAGS;

      const shouldInitializeFolders =
        contextType === 1 &&
        selectedFolders.length === 0 &&
        ids.length > 0 &&
        selectionMethod === DocumentSelectionMethod.FOLDERS;

      // Determine if we're using lazy loading
      const isLargeData = countNodes(fullTreeRef.current) > 2000;

      if (shouldInitializeSearches && treeData.length > 0) {
        let finalIds = [...ids];
        if (searchesFullTree.length > 0) {
          const state = getParentCheckboxState(searchesFullTree, ids);
          if (isLargeData && ids.length > 0) {
            // Only add root parents that are fully checked; partial selection stays in indeterminateSearches
            finalIds = [...new Set([...ids, ...state.checked])];
          }
          setIndeterminateSearches(state.indeterminate);
        } else {
          setIndeterminateSearches([]);
        }
        setSelectedSearches(finalIds);
        setValue('selectedSearches', finalIds);
      } else if (shouldInitializeTags && treeData.length > 0) {
        let finalIds = [...ids];
        // For lazy loading, include root-most parent nodes if any of their children are selected
        // This ensures root nodes appear checked on page load even before expanding
        if (isLargeData && fullTreeRef.current.length > 0 && ids.length > 0) {
          const parentNodeIds = findParentNodeIds(fullTreeRef.current, ids);
          // Combine selected items with their parent nodes (including root-most), remove duplicates
          finalIds = [...new Set([...ids, ...parentNodeIds])];
        }
        setSelectedTags(finalIds);
        setValue('selectedTags', finalIds);
      } else if (shouldInitializeFolders && treeData.length > 0) {
        let finalIds = [...ids];
        // For lazy loading, include root-most parent nodes if any of their children are selected
        // This ensures root nodes appear checked on page load even before expanding
        if (isLargeData && fullTreeRef.current.length > 0 && ids.length > 0) {
          const parentNodeIds = findParentNodeIds(fullTreeRef.current, ids);
          // Combine selected items with their parent nodes (including root-most), remove duplicates
          finalIds = [...new Set([...ids, ...parentNodeIds])];
        }
        setSelectedFolders(finalIds);
        setValue('selectedFolders', finalIds);
      }
    }, [treeData, documentSelectionData, selectionMethod]);

    // Expand all nodes by default
    const allExpandedIds = useMemo(() => {
      const ids: string[] = [];
      const traverse = (nodes: TreeViewBaseItem<ExtendedTreeItemProps>[]) => {
        for (const node of nodes) {
          ids.push(node.id);
          if (node.children?.length) {
            traverse(node.children);
          }
        }
      };

      if (treeData.length > 0) {
        traverse(treeData);
      }

      return ids;
    }, [treeData]);

    // Determine if we should use lazy loading based on full tree size
    const isLargeData = useMemo(() => {
      return countNodes(fullTreeRef.current) > 2000;
    }, [treeData]);

    // For ArrLazyTreeView expansion control
    const [expandedNodes, setExpandedNodes] = useState<string[]>([]);

    // Local state for lazy tree rendering; lets ArrLazyTreeView push updates via updateTreeData
    const [lazyTreeData, setLazyTreeData] = useState<
      TreeViewBaseItem<ExtendedTreeItemProps>[]
    >([]);

    // Refs for stable updateTreeData callback to avoid infinite update loop
    const lazyTreeDataRef = useRef(lazyTreeData);
    const isInProgressOrCompletedRef = useRef(isInProgressOrCompleted);
    lazyTreeDataRef.current = lazyTreeData;
    isInProgressOrCompletedRef.current = isInProgressOrCompleted;
    // Once the child (ArrLazyTreeView) has pushed tree with loaded children, never overwrite with initial placeholder
    const lazyTreeUpdatedByChildRef = useRef(false);

    // Initialize/reset lazy tree source whenever large data toggles or underlying tree changes
    useEffect(() => {
      if (isLargeData) {
        if (
          lazyTreeData.length === 0 &&
          treeData.length > 0 &&
          !lazyTreeUpdatedByChildRef.current
        ) {
          const withPlaceholders = treeData.map((item: TreeItem) => {
            const hasKids =
              Array.isArray(item.children) && item.children.length > 0;
            return {
              ...item,
              hasChildren: hasKids,
              children: hasKids
                ? ([
                    {
                      id: `${item.id}-loading`,
                      label: 'Loading...',
                      disabled: true,
                    } as TreeItem,
                  ] as TreeItem[])
                : undefined,
              isChildrenLoaded: false,
            } as TreeItem;
          });
          setLazyTreeData(withPlaceholders);
          if (expandedNodes.length === 0) {
            setExpandedNodes(treeData.map((item: TreeItem) => item.id)); // show roots
          }
        }
      } else if (lazyTreeData.length) {
        setLazyTreeData([]);
        setExpandedNodes([]);
        lazyTreeUpdatedByChildRef.current = false;
      }
    }, [isLargeData, treeData]);

    // When selection method (Tags/Searches/Folders) changes, clear lazy tree so it re-inits for the new list
    useEffect(() => {
      setLazyTreeData([]);
      setExpandedNodes([]);
      lazyTreeUpdatedByChildRef.current = false;
    }, [selectionMethod]);

    // Helper to mark all nodes disabled (used for stable callback and display)
    const markTreeDisabled = useCallback(
      (
        nodes: TreeViewBaseItem<ExtendedTreeItemProps>[],
        disabled: boolean
      ): TreeViewBaseItem<ExtendedTreeItemProps>[] =>
        disabled
          ? nodes.map((n) => ({
              ...n,
              disabled: true,
              children: n.children
                ? markTreeDisabled(n.children, true)
                : n.children,
            }))
          : nodes,
      []
    );

    // Stable callback so ArrLazyTreeView does not trigger infinite update loop
    const handleUpdateTreeData = useCallback(
      (
        data: React.SetStateAction<TreeViewBaseItem<ExtendedTreeItemProps>[]>
      ) => {
        if (data === undefined) return;
        const next =
          typeof data === 'function'
            ? ((
                data as (
                  prev: TreeViewBaseItem<ExtendedTreeItemProps>[]
                ) => TreeViewBaseItem<ExtendedTreeItemProps>[]
              )(lazyTreeDataRef.current) ?? [])
            : data;
        const toSet = isInProgressOrCompletedRef.current
          ? markTreeDisabled(next, true)
          : next;
        lazyTreeUpdatedByChildRef.current = true;
        setLazyTreeData(toSet);
      },
      [markTreeDisabled]
    );

    const {
      handleSubmit,
      setValue,
      reset,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      formState: {errors, isSubmitting, isDirty, isValid},
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      watch,
    } = useForm<DocumentSelectionFormData>({
      mode: 'onChange',
      resolver: zodResolver(documentSelectionSchema()),
      defaultValues: {
        action: FormAction.SAVE,
        selectionMethod: DocumentSelectionMethod.TAGS,
        selectedTags: [],
        selectedSearches: [],
        selectedFolders: [],
        includeFamilies: false,
      },
    });

    useEffect(() => {
      if (componentIsDirty) {
        dispatch(setIsFieldsChanged({isFieldsChanged: true}));
      }
    }, [componentIsDirty]);

    // Reset isSaved when form becomes dirty (user makes changes)
    useEffect(() => {
      if (isDirty && isSaved) {
        setIsSaved(false);
      }
    }, [isDirty, isSaved]);

    const lastInitRef = useRef<string | null>(null);
    const lastProductionIdRef = useRef<number | null>(null);
    const lastContextTypeRef = useRef<number | null>(null);
    const isUpdatingDocCountRef = useRef<boolean>(false);
    const isUserClearedRef = useRef<boolean>(false);
    const hasUserSelectedRadioRef = useRef<boolean>(false);
    const hasUserInteractedRef = useRef<boolean>(false); // Track if user has made any selection changes
    const hasAutoRecountedRef = useRef<boolean>(false);

    // Reset cache when productionId changes
    useEffect(() => {
      if (productionId > 0 && lastProductionIdRef.current !== productionId) {
        lastInitRef.current = null;
        lastProductionIdRef.current = productionId;
        lastContextTypeRef.current = null;
        isUserClearedRef.current = false;
        setHasUserSelectedRadio(false);
        hasUserSelectedRadioRef.current = false;
        hasUserInteractedRef.current = false;
        lazyTreeUpdatedByChildRef.current = false;
        hasAutoRecountedRef.current = false;
        setLastCountSnapshot({tags: [], searches: [], folders: []});
      }
    }, [productionId]);

    useEffect(() => {
      const selection = documentSelectionData?.productionDocumentsSelection;
      if (!selection) return;

      const contextType = selection.contextType || 0;
      const currentKey = `${productionId}-${contextType}`;

      const isInitialLoad = lastInitRef.current === null;
      const isProductionChanged = lastProductionIdRef.current !== productionId;
      const isNewDataSource = isInitialLoad || isProductionChanged;

      const responseTotalDocs = selection.totalNumberOfDocsIncludingFamily ?? 0;
      const responseFamilyDocs = selection.totalNumberOfFamilyDocs ?? 0;

      if (lastInitRef.current === currentKey && !isNewDataSource) {
        setTotalDocs(responseTotalDocs);
        setFamilyDocs(responseFamilyDocs);
        if (!isUpdatingDocCountRef.current) {
          setIncludeFamilies(selection.toIncludeFamilies || false);
          setValue('includeFamilies', selection.toIncludeFamilies || false);
        }

        if (isUpdatingDocCountRef.current) {
          isUpdatingDocCountRef.current = false;
        }
        return;
      }

      lastInitRef.current = currentKey;
      lastContextTypeRef.current = contextType;

      const hasNoSavedData =
        contextType === 0 &&
        (!selection.contextIDs ||
          selection.contextIDs.length === 0 ||
          selection.contextIDs === null);

      if (!isUpdatingDocCountRef.current) {
        // If no saved data, ensure includeFamilies is OFF
        if (hasNoSavedData) {
          setIncludeFamilies(false);
          setValue('includeFamilies', false);
        } else {
          setIncludeFamilies(selection.toIncludeFamilies || false);
          setValue('includeFamilies', selection.toIncludeFamilies || false);
        }
      }
      const newTotalDocs = selection.totalNumberOfDocsIncludingFamily || 0;
      const newFamilyDocs = selection.totalNumberOfFamilyDocs || 0;

      setTotalDocs(newTotalDocs);
      setFamilyDocs(newFamilyDocs);

      if (isNewDataSource && !isUpdatingDocCountRef.current) {
        setSelectedTags([]);
        setSelectedSearches([]);
        setSelectedFolders([]);
        setIndeterminateSearches([]);
        setValue('selectedTags', []);
        setValue('selectedSearches', []);
        setValue('selectedFolders', []);

        // Check if there's no saved data (contextType: 0 and contextIDs: null/empty)
        const hasNoSavedData =
          contextType === 0 &&
          (!selection.contextIDs ||
            selection.contextIDs.length === 0 ||
            selection.contextIDs === null);

        if (hasNoSavedData) {
          setSelectionMethod(DocumentSelectionMethod.TAGS);
          setValue('selectionMethod', DocumentSelectionMethod.TAGS, {
            shouldDirty: false,
          });
          // Reset radio interaction flag since this is a new load
          setHasUserSelectedRadio(false);
          hasUserSelectedRadioRef.current = false;
          setLastCountSnapshot({tags: [], searches: [], folders: []});
        } else {
          let loadedTags: string[] = [];
          let loadedSearches: string[] = [];
          let loadedFolders: string[] = [];

          if (contextType === 2) {
            // TAGS
            setSelectionMethod(DocumentSelectionMethod.TAGS);
            setValue('selectionMethod', DocumentSelectionMethod.TAGS);
            if (selection.contextIDs && selection.contextIDs.length > 0) {
              const ids = selection.contextIDs.map(String);
              setSelectedTags(ids);
              setValue('selectedTags', ids);
              loadedTags = ids;
            }
          } else if (contextType === 1) {
            // FOLDERS
            setSelectionMethod(DocumentSelectionMethod.FOLDERS);
            setValue('selectionMethod', DocumentSelectionMethod.FOLDERS);
            if (selection.contextIDs && selection.contextIDs.length > 0) {
              const ids = selection.contextIDs.map(String);
              setSelectedFolders(ids);
              setValue('selectedFolders', ids);
              loadedFolders = ids;
            }
          } else if (contextType === 3) {
            // SEARCHES
            setSelectionMethod(DocumentSelectionMethod.SEARCHES);
            setValue('selectionMethod', DocumentSelectionMethod.SEARCHES);
            if (selection.contextIDs && selection.contextIDs.length > 0) {
              const ids = selection.contextIDs.map(String);

              // Calculate root parent states (checked and indeterminate)
              if (searchesFullTree.length > 0) {
                const state = getParentCheckboxState(searchesFullTree, ids);
                const finalIds = [...new Set([...ids, ...state.checked])];
                setSelectedSearches(finalIds);
                setValue('selectedSearches', finalIds);
                setIndeterminateSearches(state.indeterminate);
                loadedSearches = finalIds;
              } else {
                setSelectedSearches(ids);
                setValue('selectedSearches', ids);
                setIndeterminateSearches([]);
                loadedSearches = ids;
              }
            }
          } else {
            // No selection - default to TAGS
            setSelectionMethod(DocumentSelectionMethod.TAGS);
            setValue('selectionMethod', DocumentSelectionMethod.TAGS);
          }
          // Mark that we have saved data, so radio should show as selected
          setHasUserSelectedRadio(true);
          hasUserSelectedRadioRef.current = true;
          setLastCountSnapshot({
            tags: loadedTags,
            searches: loadedSearches,
            folders: loadedFolders,
          });
        }
      } else {
        const hasCurrentSelections =
          (selectionMethod === DocumentSelectionMethod.TAGS &&
            selectedTags.length > 0) ||
          (selectionMethod === DocumentSelectionMethod.SEARCHES &&
            selectedSearches.length > 0) ||
          (selectionMethod === DocumentSelectionMethod.FOLDERS &&
            selectedFolders.length > 0);

        const hasApiSelections =
          selection.contextIDs && selection.contextIDs.length > 0;

        if (
          hasApiSelections &&
          !hasCurrentSelections &&
          !isUpdatingDocCountRef.current &&
          !isUserClearedRef.current &&
          !hasUserInteractedRef.current
        ) {
          const ids = selection.contextIDs.map(String);
          let loadedTags: string[] = [];
          let loadedSearches: string[] = [];
          let loadedFolders: string[] = [];

          if (contextType === 2) {
            setSelectedTags(ids);
            setValue('selectedTags', ids);
            loadedTags = ids;
          } else if (contextType === 1) {
            setSelectedFolders(ids);
            setValue('selectedFolders', ids);
            loadedFolders = ids;
          } else if (contextType === 3) {
            // Calculate root parent states (checked and indeterminate)
            if (searchesFullTree.length > 0) {
              const state = getParentCheckboxState(searchesFullTree, ids);
              const finalIds = [...new Set([...ids, ...state.checked])];
              setSelectedSearches(finalIds);
              setValue('selectedSearches', finalIds);
              setIndeterminateSearches(state.indeterminate);
              loadedSearches = finalIds;
            } else {
              setSelectedSearches(ids);
              setValue('selectedSearches', ids);
              setIndeterminateSearches([]);
              loadedSearches = ids;
            }
            // Also set selection method if it's different
            if (selectionMethod !== DocumentSelectionMethod.SEARCHES) {
              setSelectionMethod(DocumentSelectionMethod.SEARCHES);
              setValue('selectionMethod', DocumentSelectionMethod.SEARCHES);
            }
          }

          setLastCountSnapshot({
            tags: loadedTags,
            searches: loadedSearches,
            folders: loadedFolders,
          });
        }

        setTotalDocs(selection.totalNumberOfDocsIncludingFamily || 0);
        setFamilyDocs(selection.totalNumberOfFamilyDocs || 0);
        if (!isUpdatingDocCountRef.current) {
          setIncludeFamilies(selection.toIncludeFamilies || false);
          setValue('includeFamilies', selection.toIncludeFamilies || false);
        }
        if (isUpdatingDocCountRef.current) {
          isUpdatingDocCountRef.current = false;
        }
      }
    }, [documentSelectionData, productionId, setValue]);

    /*
      If a saved selection already exists, silently recompute its counts
      once on load so family docs is always accurate, regardless of what
      the initial GET response already returned for totalDocs/familyDocs.
    */
    useEffect(() => {
      if (isDocumentSelectionFetching) return;
      if (hasAutoRecountedRef.current) return;

      const selection = documentSelectionData?.productionDocumentsSelection;
      if (!selection) return;

      const rawContextType = selection.contextType || 0;
      const hasSavedSelection =
        rawContextType !== 0 &&
        !!selection.contextIDs &&
        selection.contextIDs.length > 0;

      if (!hasSavedSelection) return;

      const contextTypeMap: Record<number, DocumentSelectionApiContextType> = {
        1: DocumentSelectionApiContextType.FOLDERS,
        2: DocumentSelectionApiContextType.TAGS,
        3: DocumentSelectionApiContextType.SEARCHES,
      };
      const payloadContextType = contextTypeMap[rawContextType];
      if (!payloadContextType) return;

      hasAutoRecountedRef.current = true;
      handleUpdateDocCount({
        silent: true,
        realIncludeFamilies: selection.toIncludeFamilies || false,
        override: {
          contextType: payloadContextType,
          contextIDs: selection.contextIDs
            .map(Number)
            .filter((id) => !isNaN(id)),
        },
      });
    }, [documentSelectionData, productionId, isDocumentSelectionFetching]);

    // Handle selection method change
    const RADIO_TO_ENUM: Record<string, DocumentSelectionMethod> = {
      rdbTags: DocumentSelectionMethod.TAGS,
      rdbSearches: DocumentSelectionMethod.SEARCHES,
      rdbFolders: DocumentSelectionMethod.FOLDERS,
    };

    const ENUM_TO_RADIO: Partial<Record<DocumentSelectionMethod, string>> = {
      [DocumentSelectionMethod.TAGS]: 'rdbTags',
      [DocumentSelectionMethod.SEARCHES]: 'rdbSearches',
      [DocumentSelectionMethod.FOLDERS]: 'rdbFolders',
    } as const;

    const [hasUserSelectedRadio, setHasUserSelectedRadio] =
      useState<boolean>(false);

    const hasNoSavedDataFromApi = useMemo(() => {
      // If user has clicked a radio button, always show it as selected
      if (hasUserSelectedRadio) {
        return false;
      }

      // If there are any selections, user has interacted, show selected radio
      if (
        selectedTags.length > 0 ||
        selectedSearches.length > 0 ||
        selectedFolders.length > 0
      ) {
        return false;
      }

      const selection = documentSelectionData?.productionDocumentsSelection;
      if (!selection) return true;

      const contextType = selection.contextType || 0;
      const hasNoData =
        contextType === 0 &&
        (!selection.contextIDs ||
          selection.contextIDs.length === 0 ||
          selection.contextIDs === null);

      return hasNoData;
    }, [
      documentSelectionData,
      selectedTags,
      selectedSearches,
      selectedFolders,
      hasUserSelectedRadio,
    ]);

    const handleSelectionMethodChange = (value: string) => {
      setComponentIsDirty(true);
      const method = RADIO_TO_ENUM[value] ?? DocumentSelectionMethod.TAGS;
      setSelectionMethod(method);
      setValue('selectionMethod', method, {shouldDirty: true});
      setHasUserSelectedRadio(true);
      hasUserSelectedRadioRef.current = true;
    };

    // Handle tree selection change (works for all three types)
    const handleTreeSelectionChange = (selectedIds: string[] | null) => {
      const ids = selectedIds || [];

      // Get current selection before change for comparison
      let currentSelection: string[] = [];

      switch (selectionMethod) {
        case DocumentSelectionMethod.TAGS:
          currentSelection = selectedTags;
          break;
        case DocumentSelectionMethod.SEARCHES:
          currentSelection = selectedSearches;
          break;
        case DocumentSelectionMethod.FOLDERS:
          currentSelection = selectedFolders;
          break;
      }

      hasUserInteractedRef.current = true;

      if (isUserClearedRef.current && ids.length > 0) {
        isUserClearedRef.current = false;
      }

      // Determine if we're using lazy loading
      const isLargeData = countNodes(fullTreeRef.current) > 2000;
      let processedIds = [...ids];

      // Handle root node selections: if a root node (top-level in treeData) is selected,
      // find all its children in the full tree and include them in the selection
      // BUT only if the root node was explicitly clicked (newly selected), not if it's
      // just included for display because its children are selected
      if (
        isLargeData &&
        fullTreeRef.current.length > 0 &&
        treeData.length > 0
      ) {
        // Get root node IDs (top-level nodes in treeData)
        const rootNodeIds = treeData.map((item) => String(item.id));

        // Check if any selected IDs are root nodes
        const selectedRootNodeIds = ids.filter((id) =>
          rootNodeIds.includes(String(id))
        );

        // Check if any root nodes were unchecked (were in currentSelection but not in ids)
        const uncheckedRootNodeIds = rootNodeIds.filter(
          (rootId) =>
            currentSelection.includes(String(rootId)) &&
            !ids.includes(String(rootId))
        );

        // Handle unchecked root nodes: remove all their children
        if (uncheckedRootNodeIds.length > 0) {
          for (const rootId of uncheckedRootNodeIds) {
            const rootNode = findNode(fullTreeRef.current, String(rootId));
            if (rootNode) {
              const childIds = getAllChildNodeIds(rootNode);
              // Remove all children of unchecked root nodes from processedIds
              processedIds = processedIds.filter(
                (id) => !childIds.includes(String(id))
              );
            }
          }
        }

        if (selectedRootNodeIds.length > 0) {
          // Only include children if the root node was explicitly clicked (newly selected)
          // If the root node was already in currentSelection, it's a display-only parent
          // and we shouldn't treat it as if it was explicitly clicked
          const newlySelectedRootNodeIds = selectedRootNodeIds.filter(
            (rootId) => !currentSelection.includes(String(rootId))
          );

          if (newlySelectedRootNodeIds.length > 0) {
            // For each newly selected root node, check if it was explicitly clicked
            // vs. just included for display because its children were selected
            for (const rootId of newlySelectedRootNodeIds) {
              const rootNode = findNode(fullTreeRef.current, String(rootId));
              if (rootNode) {
                // Get all children of this root node
                const childIds = getAllChildNodeIds(rootNode);
                // Check if any of the root's children are in the newly selected items
                // If children are being selected, the parent is likely included for display, not explicitly clicked
                const newlySelectedChildIds = ids.filter(
                  (id) =>
                    childIds.includes(String(id)) &&
                    !currentSelection.includes(String(id))
                );

                // Only include all children if the root was explicitly clicked
                // (i.e., no children are being newly selected - the root was clicked directly)
                if (newlySelectedChildIds.length === 0) {
                  // Root node was explicitly clicked, include all its children
                  // Ensure the root node itself is in processedIds (it should be from ids, but double-check)
                  if (!processedIds.includes(String(rootId))) {
                    processedIds.push(String(rootId));
                  }
                  // Add all children
                  if (childIds.length > 0) {
                    processedIds.push(...childIds);
                  }
                }
                // If children are being selected, don't include all children
                // The parent is just included for display
              }
            }
            // Remove duplicates
            processedIds = [...new Set(processedIds)];
          }
        }
      }

      // Filter out loading placeholder IDs but keep all other IDs
      const validIds = processedIds.filter((id) => {
        if (!id) return false;
        const idStr = String(id).trim();
        if (idStr === '') return false;
        // Skip loading placeholder IDs (e.g., "nodeId-loading")
        if (idStr.includes('-loading')) return false;
        // Keep all other IDs (numeric, alphanumeric, etc.)
        return true;
      });

      // --- AUTO UNCHECK PARENT IF NO CHILD SELECTED ---
      // But only if the parent wasn't explicitly clicked by the user
      // Track which nodes were newly selected (explicitly clicked) in this change
      const newlySelectedIds = new Set(
        validIds.filter((id) => !currentSelection.includes(id))
      );

      // Track which nodes were explicitly unchecked (removed by user)
      const explicitlyUncheckedIds = new Set(
        currentSelection.filter((id) => !validIds.includes(id))
      );

      // For lazy loading, track which root nodes were explicitly clicked
      const explicitlyClickedRootNodes = new Set<string>();
      if (isLargeData && treeData.length > 0) {
        const rootNodeIds = treeData.map((item) => String(item.id));
        rootNodeIds.forEach((rootId) => {
          if (newlySelectedIds.has(rootId)) {
            explicitlyClickedRootNodes.add(rootId);
          }
        });
      }

      const autoUncheckParents = (
        node: TreeViewBaseItem<ExtendedTreeItemProps>
      ) => {
        if (!node.children || node.children.length === 0) return;

        // Get all real (non-loading) children from the full tree
        const childIds = getAllChildNodeIds(node);

        const anyChildSelected = childIds.some((id) => validIds.includes(id));
        const parentIndex = validIds.indexOf(node.id);
        const isParentExplicitlyClicked = newlySelectedIds.has(node.id);
        const isParentExplicitlyUnchecked = explicitlyUncheckedIds.has(node.id);

        // Check if this is a root node
        const isRootNode =
          isLargeData &&
          treeData.length > 0 &&
          treeData.some((item) => String(item.id) === String(node.id));

        // Check if this root node was explicitly clicked
        const isRootExplicitlyClicked =
          isRootNode && explicitlyClickedRootNodes.has(String(node.id));

        // Only remove parent if:
        // 1. No children are selected
        // 2. Parent is in validIds (was selected)
        // 3. Parent was NOT explicitly clicked by the user (it was added for display only)
        // 4. Parent was NOT explicitly unchecked (user already handled it)
        // Exception: NEVER remove root nodes that were explicitly clicked (even if children aren't loaded yet)
        if (
          !anyChildSelected &&
          parentIndex !== -1 &&
          !isParentExplicitlyClicked &&
          !isParentExplicitlyUnchecked &&
          !isRootExplicitlyClicked
        ) {
          // For root nodes, be very conservative - only remove if we're absolutely sure it's display-only
          if (isRootNode) {
            // For root nodes, check multiple conditions before removing:
            // 1. Was NOT in previous selection (wasn't explicitly selected before)
            // 2. Was NOT explicitly clicked in this change
            // 3. No children are selected
            const wasInPreviousSelection = currentSelection.includes(node.id);
            if (!wasInPreviousSelection && !isParentExplicitlyClicked) {
              // Wasn't in previous selection and wasn't explicitly clicked = display-only, remove it
              validIds.splice(parentIndex, 1);
            }
            // If it was in previous selection OR explicitly clicked, keep it
          } else {
            // For non-root nodes, remove if no children selected and not explicitly clicked
            validIds.splice(parentIndex, 1);
          }
        }

        // Recursively check deeper levels
        node.children.forEach((child) => {
          autoUncheckParents(child);
        });
      };

      // Apply auto-uncheck to entire tree
      fullTreeRef.current.forEach((root) => autoUncheckParents(root));

      // Defer state updates to avoid setState during render
      setTimeout(() => {
        switch (selectionMethod) {
          case DocumentSelectionMethod.TAGS:
            setSelectedTags(validIds);
            setValue('selectedTags', validIds, {shouldDirty: true});
            break;
          case DocumentSelectionMethod.SEARCHES:
            setSelectedSearches(validIds);
            setValue('selectedSearches', validIds, {shouldDirty: true});
            break;
          case DocumentSelectionMethod.FOLDERS:
            setSelectedFolders(validIds);
            setValue('selectedFolders', validIds, {shouldDirty: true});
            break;
        }
      }, 0);
    };

    const handleClear = () => {
      // Set flags to prevent re-initialization from API after clear
      isUserClearedRef.current = true;
      hasUserInteractedRef.current = true; // Mark as user interaction

      setComponentIsDirty(true);

      // Clear all selections from state
      setSelectedTags([]);
      setSelectedSearches([]);
      setSelectedFolders([]);
      setIndeterminateSearches([]);

      // Clear all form values
      setValue('selectedTags', [], {shouldDirty: true});
      setValue('selectedSearches', [], {shouldDirty: true});
      setValue('selectedFolders', [], {shouldDirty: true});

      // Reset counts to 0
      setTotalDocs(0);
      setFamilyDocs(0);

      setLastCountSnapshot({tags: [], searches: [], folders: []});
    };

    const resolveDocListErrorMessage = () =>
      t(
        'productionsAndExports.createNewProduction.documentSelection.docListError',
        'Unable to open document list'
      );

    const handleViewDocList = () => {
      if (isDocListLoading) return;
      fetchDocumentList(documentListPayload, {
        onSuccess: (response) => {
          const masterIdRaw =
            response.docId ??
            response.doclistId ??
            response.documentListID ??
            response.defaultMasterID ??
            response.docViewSessionObjectsRes?.documentListMasterID ??
            0;
          const masterId = Number(masterIdRaw) || 0;

          if (!masterId) {
            getStatus(true, ToastType.Error, resolveDocListErrorMessage());
            return;
          }

          clearDocViewState();
          dispatch(storeDocumentListMasterId(String(masterId)));
          dispatch(storeSourceTypeCode(SourceTypeCode.PRODUCTIONTOTAL));
          dispatch(
            setDocListBackNavigation({
              label: t('docList.backSource.production'),
              path: `${window.location.pathname}${window.location.search}`,
            })
          );
          dispatch(setDocListId(masterId));
          navigate('/Document/DocList');
        },
        onError: (error) => {
          getStatus(
            true,
            ToastType.Error,
            error?.message ?? resolveDocListErrorMessage()
          );
        },
      });
    };

    const handleViewFamilyDocList = () => {
      if (isDocListLoading) return;
      fetchDocumentList(familyDocumentListPayload, {
        onSuccess: (response) => {
          const masterIdRaw =
            response.docId ??
            response.doclistId ??
            response.documentListID ??
            response.defaultMasterID ??
            response.docViewSessionObjectsRes?.documentListMasterID ??
            0;
          const masterId = Number(masterIdRaw) || 0;

          if (!masterId) {
            getStatus(true, ToastType.Error, resolveDocListErrorMessage());
            return;
          }

          clearDocViewState();
          dispatch(storeDocumentListMasterId(String(masterId)));
          dispatch(storeSourceTypeCode(SourceTypeCode.PRODUCTIONFAMILY));
          dispatch(
            setDocListBackNavigation({
              label: t('docList.backSource.production'),
              path: `${window.location.pathname}${window.location.search}`,
            })
          );
          dispatch(setDocListId(masterId));
          navigate('/Document/DocList');
        },
        onError: (error) => {
          getStatus(
            true,
            ToastType.Error,
            error?.message ?? resolveDocListErrorMessage()
          );
        },
      });
    };

    // Helper function to reset selections from inactive radio buttons
    const resetInactiveSelections = useCallback(() => {
      switch (selectionMethod) {
        case DocumentSelectionMethod.TAGS:
          // Clear searches and folders, keep tags
          setSelectedSearches([]);
          setSelectedFolders([]);
          setIndeterminateSearches([]);
          setValue('selectedSearches', []);
          setValue('selectedFolders', []);
          break;
        case DocumentSelectionMethod.SEARCHES:
          // Clear tags and folders, keep searches
          setSelectedTags([]);
          setSelectedFolders([]);
          setValue('selectedTags', []);
          setValue('selectedFolders', []);
          // Note: Don't clear indeterminateSearches here - it's tied to selectedSearches
          break;
        case DocumentSelectionMethod.FOLDERS:
          // Clear tags and searches, keep folders
          setSelectedTags([]);
          setSelectedSearches([]);
          setIndeterminateSearches([]);
          setValue('selectedTags', []);
          setValue('selectedSearches', []);
          break;
      }
    }, [selectionMethod, setValue]);

    /*
      After forcing toIncludeFamilies: true so the API returns an accurate
      family count, correct the persisted flag back to its real value if
      that real value is actually false — otherwise the DB is left thinking
      "include families" is on for this production even though it isn't,
      since the count call above is a real save, not just a preview.
    */
    const persistRealIncludeFamiliesFlag = (
      realIncludeFamilies: boolean,
      ctxType: DocumentSelectionApiContextType,
      ctxIDs: number[],
      finalTotalDocs: number,
      finalFamilyDocs: number
    ) => {
      if (realIncludeFamilies) return;

      pendingActionRef.current = 'updateDocCount';
      saveDocumentsSelection(
        {
          productionId,
          isMarkComplete: true,
          securityGroupId: selectedSecurityGroupId,
          exportProductionId: exportProductionId || 0,
          productionDocumentsSelectionRequest: {
            totalNumberOfDocsIncludingFamily: finalTotalDocs,
            totalNumberOfFamilyDocs: finalFamilyDocs,
            toIncludeFamilies: false,
            contextType: ctxType,
            contextIDs: ctxIDs,
          },
        },
        {
          onSuccess: () => {
            pendingActionRef.current = null;
          },
          onError: () => {
            pendingActionRef.current = null;
          },
        }
      );
    };

    // Handle update doc count
    const handleUpdateDocCount = (options?: {
      silent?: boolean;
      override?: {
        contextType: DocumentSelectionApiContextType;
        contextIDs: number[];
      };
      realIncludeFamilies?: boolean;
    }) => {
      const {silent = false, override} = options || {};
      const realIncludeFamilies =
        options?.realIncludeFamilies ?? includeFamilies;

      let contextType = DocumentSelectionApiContextType.TAGS;
      let contextIDs: number[] = [];
      let snapshotTags: string[] = [];
      let snapshotSearches: string[] = [];
      let snapshotFolders: string[] = [];

      if (override) {
        contextType = override.contextType;
        contextIDs = override.contextIDs;
      } else {
        setComponentIsDirty(true);

        switch (selectionMethod) {
          case DocumentSelectionMethod.TAGS:
            contextType = DocumentSelectionApiContextType.TAGS;
            contextIDs = selectedTags.map(Number).filter((id) => !isNaN(id));
            snapshotTags = selectedTags;
            break;
          case DocumentSelectionMethod.SEARCHES:
            contextType = DocumentSelectionApiContextType.SEARCHES;
            contextIDs = selectedSearches
              .map(Number)
              .filter((id) => !isNaN(id));
            snapshotSearches = selectedSearches;
            break;
          case DocumentSelectionMethod.FOLDERS:
            contextType = DocumentSelectionApiContextType.FOLDERS;
            contextIDs = selectedFolders.map(Number).filter((id) => !isNaN(id));
            snapshotFolders = selectedFolders;
            break;
        }
      }

      const payload = {
        productionId: productionId,
        isMarkComplete: true,
        securityGroupId: selectedSecurityGroupId,
        exportProductionId: exportProductionId || 0,
        productionDocumentsSelectionRequest: {
          totalNumberOfDocsIncludingFamily: -1,
          totalNumberOfFamilyDocs: -1,
          toIncludeFamilies: true,
          contextType: contextType,
          contextIDs: contextIDs,
        },
      };

      // Set a flag to indicate this is an update doc count action
      pendingActionRef.current = 'updateDocCount';
      isUpdatingDocCountRef.current = true; // Preserve selections when view API refetches

      saveDocumentsSelection(payload, {
        onSuccess: () => {
          if (!override) {
            resetInactiveSelections();
            setLastCountSnapshot({
              tags: snapshotTags,
              searches: snapshotSearches,
              folders: snapshotFolders,
            });
          }

          // Await the refetch to ensure it completes and triggers useEffect
          // Pass { cancelRefetch: false } to ensure refetch happens even if there's a pending request
          refetchDocumentSelection({cancelRefetch: false})
            .then((result) => {
              const selection = result.data?.productionDocumentsSelection;
              const newTotalDocs =
                selection?.totalNumberOfDocsIncludingFamily ?? totalDocs;
              const newFamilyDocs =
                selection?.totalNumberOfFamilyDocs ?? familyDocs;

              // Force update counts directly from refetch result if useEffect didn't trigger
              // Only update if values are different to avoid unnecessary re-renders
              if (newTotalDocs !== totalDocs || newFamilyDocs !== familyDocs) {
                setTotalDocs(newTotalDocs);
                setFamilyDocs(newFamilyDocs);
              }

              persistRealIncludeFamiliesFlag(
                realIncludeFamilies,
                contextType,
                contextIDs,
                newTotalDocs,
                newFamilyDocs
              );
            })
            .catch((error) => {
              console.error('[UPDATE DOC COUNT] Refetch error', error);
            });
          if (!silent) {
            getStatus(
              true,
              ToastType.Success,
              t(
                'productionsAndExports.createNewProduction.documentSelection.infoSuccess'
              )
            );
          }
          pendingActionRef.current = null;
        },
        onError: () => {
          pendingActionRef.current = null;
          isUpdatingDocCountRef.current = false;
        },
      });
    };

    // Handle include families toggle
    const handleIncludeFamiliesChange = (
      event: React.ChangeEvent<HTMLInputElement>
    ) => {
      const checked = event.target.checked;
      setIncludeFamilies(checked);
      setValue('includeFamilies', checked, {shouldDirty: true});
    };

    const displayedTotalDocs = includeFamilies
      ? totalDocs
      : Math.max(totalDocs - familyDocs, 0);
    const initialLazyTreeData = useMemo(() => {
      return treeData.map((item: TreeItem) => {
        const hasKids =
          Array.isArray(item.children) && item.children.length > 0;
        return {
          ...item,
          hasChildren: hasKids,
          children: hasKids
            ? [{id: `${item.id}-loading`, label: 'Loading...', disabled: true}]
            : undefined,
          isChildrenLoaded: false,
          disabled: isInProgressOrCompleted,
        } as TreeItem;
      });
    }, [treeData, isInProgressOrCompleted]);

    // Disable all tree items if isInProgressOrCompleted
    const displayedTreeData = useMemo(() => {
      if (!isInProgressOrCompleted) return treeData;
      const markDisabled = (
        nodes: TreeViewBaseItem<ExtendedTreeItemProps>[]
      ): TreeViewBaseItem<ExtendedTreeItemProps>[] =>
        nodes.map((n) => ({
          ...n,
          disabled: isInProgressOrCompleted,
          children: n.children ? markDisabled(n.children) : n.children,
        }));

      return markDisabled(treeData);
    }, [treeData, isInProgressOrCompleted]);

    // Return stable reference when source and isInProgressOrCompleted unchanged to prevent update loop
    const displayedLazyItems = useMemo(() => {
      const source = lazyTreeData.length ? lazyTreeData : initialLazyTreeData;
      if (!isInProgressOrCompleted) return source;
      const cache = displayedLazyItemsCacheRef.current;
      if (
        cache.source === source &&
        cache.disabled === isInProgressOrCompleted
      ) {
        return cache.result;
      }
      const result = markTreeDisabled(source, true);
      displayedLazyItemsCacheRef.current = {
        source,
        disabled: isInProgressOrCompleted,
        result,
      };
      return result;
    }, [
      lazyTreeData,
      initialLazyTreeData,
      isInProgressOrCompleted,
      markTreeDisabled,
    ]);

    // Button enabling logic per story
    // Check only the active selection method (current radio button selection)
    const hasAnySelection = selectedItems.length > 0;

    const arraysEqual = (a: string[], b: string[]) =>
      a.length === b.length && a.every((v, i) => v === b[i]);

    const hasUncountedChanges =
      hasAnySelection &&
      (() => {
        switch (selectionMethod) {
          case DocumentSelectionMethod.TAGS:
            return !arraysEqual(selectedTags, lastCountSnapshot.tags);
          case DocumentSelectionMethod.SEARCHES:
            return !arraysEqual(selectedSearches, lastCountSnapshot.searches);
          case DocumentSelectionMethod.FOLDERS:
            return !arraysEqual(selectedFolders, lastCountSnapshot.folders);
          default:
            return false;
        }
      })();

    const isUpdateDocCountEnabled =
      hasUncountedChanges &&
      !isSavingDocumentsSelection &&
      !isDocumentSelectionLoading &&
      !isInProgressOrCompleted;

    // Save and Next disabled until user has made any selection or while saving/loading
    const isSaveDisabled =
      !hasAnySelection ||
      mode === FormMode.VIEW ||
      isSavingDocumentsSelection ||
      isDocumentSelectionLoading ||
      (!isDirty && isSaved);

    const isNextDisabled =
      !hasAnySelection ||
      isSavingDocumentsSelection ||
      isDocumentSelectionLoading;

    // Calculate shouldSkipPrivGuard - export production with prior production set
    // This determines if we should skip PrivGuard and go directly to Delivery Options
    const shouldSkipPrivGuard = useMemo(() => {
      return (
        !isProductionSet && exportProductionId > 0 && skipToDeliveryOptions
      );
    }, [isProductionSet, exportProductionId, skipToDeliveryOptions]);

    // Expose methods to parent
    useImperativeHandle(ref, () => ({
      save: () => {
        setValue('action', FormAction.SAVE);
        handleSubmit(onSubmit)();
      },
      next: () => {
        // PRIORITY 1: If shouldSkipPrivGuard is true, ALWAYS show modal
        // This takes precedence over isInProgressOrCompleted and !isUpdateNeeded
        if (shouldSkipPrivGuard) {
          if (isUpdateDocCountEnabled) {
            getStatus(
              true,
              ToastType.Warning,
              t(
                'productionsAndExports.createNewProduction.documentSelection.updateDocCountWarning'
              )
            );
            return;
          }

          // Validate form, then show modal
          // Modal OK handler will decide whether to call API or skip directly
          handleSubmit(() => {
            // Form is valid - show warning modal
            // Modal OK will handle the navigation (with or without API call)
            setShowExportWarningModal(true);
          }, onInvalid)();
          return;
        }

        // PRIORITY 2: Normal flow when shouldSkipPrivGuard is false
        // If isInProgressOrCompleted OR !isUpdateNeeded, use early return
        if (isInProgressOrCompleted || !isUpdateNeeded) {
          goNext?.();
          return;
        }

        // Continue with existing flow when update is needed and no skip required
        if (isUpdateDocCountEnabled) {
          getStatus(
            true,
            ToastType.Warning,
            t(
              'productionsAndExports.createNewProduction.documentSelection.updateDocCountWarning'
            )
          );
          return;
        }

        // Validate first before showing modal - validate form
        handleSubmit((data) => {
          // Form is valid - if exportProductionId > 0, show warning modal
          if (exportProductionId > 0) {
            setShowExportWarningModal(true);
          } else {
            // Normal flow - proceed to next
            // Set action to NEXT before calling onSubmit
            const dataWithNext = {...data, action: FormAction.NEXT};
            setValue('action', FormAction.NEXT);
            onSubmit(dataWithNext);
          }
        }, onInvalid)();
      },
    }));

    // Report state changes to parent
    useEffect(() => {
      onStateChange?.({
        saveDisabled: isSaveDisabled,
        nextDisabled: isNextDisabled,
      });
    }, [isSaveDisabled, isNextDisabled, onStateChange]);

    const onSubmit = (data: DocumentSelectionFormData) => {
      const currentSelectedTags = selectedTags;
      const currentSelectedSearches = selectedSearches;
      const currentSelectedFolders = selectedFolders;
      const currentSelectionMethod = selectionMethod || data.selectionMethod;

      const tagsIDs = currentSelectedTags
        .map(Number)
        .filter((id) => !isNaN(id));
      const searchesIDs = currentSelectedSearches
        .map(Number)
        .filter((id) => !isNaN(id));
      const foldersIDs = currentSelectedFolders
        .map(Number)
        .filter((id) => !isNaN(id));

      const isTagsSelected = tagsIDs.length > 0;
      const isSavedSearchSelected = searchesIDs.length > 0;
      const isFoldersSelected = foldersIDs.length > 0;

      // Determine primary contextType based on current selection method or first available selection
      // This is used for contextType and contextIDs in the payload
      let contextType = DocumentSelectionApiContextType.TAGS;
      let contextIDs: number[] = [];

      if (
        isTagsSelected &&
        currentSelectionMethod === DocumentSelectionMethod.TAGS
      ) {
        contextType = DocumentSelectionApiContextType.TAGS;
        contextIDs = tagsIDs;
      } else if (
        isSavedSearchSelected &&
        currentSelectionMethod === DocumentSelectionMethod.SEARCHES
      ) {
        contextType = DocumentSelectionApiContextType.SEARCHES;
        contextIDs = searchesIDs;
      } else if (
        isFoldersSelected &&
        currentSelectionMethod === DocumentSelectionMethod.FOLDERS
      ) {
        contextType = DocumentSelectionApiContextType.FOLDERS;
        contextIDs = foldersIDs;
      } else {
        if (isTagsSelected) {
          contextType = DocumentSelectionApiContextType.TAGS;
          contextIDs = tagsIDs;
        } else if (isSavedSearchSelected) {
          contextType = DocumentSelectionApiContextType.SEARCHES;
          contextIDs = searchesIDs;
        } else if (isFoldersSelected) {
          contextType = DocumentSelectionApiContextType.FOLDERS;
          contextIDs = foldersIDs;
        }
      }

      const payload = {
        productionId: productionId,
        isMarkComplete: true,
        securityGroupId: selectedSecurityGroupId,
        exportProductionId: exportProductionId || 0,
        productionDocumentsSelectionRequest: {
          totalNumberOfDocsIncludingFamily: -1,
          totalNumberOfFamilyDocs: -1,
          toIncludeFamilies: true,
          // Send all selections from all methods, not just current method
          contextType: contextType,
          contextIDs: contextIDs, // Primary context type IDs
        },
      };

      pendingSaveCorrectionRef.current = {
        realIncludeFamilies: data.includeFamilies,
        contextType,
        contextIDs,
      };
      pendingActionRef.current = data.action;
      isUpdatingDocCountRef.current = true;
      saveDocumentsSelection(payload);
      dispatch(setIsFieldsChanged({isFieldsChanged: false}));
    };

    const onInvalid = () => {
      getStatus(
        true,
        ToastType.Error,
        t(
          'productionsAndExports.createNewProduction.documentSelection.validationError'
        )
      );
    };

    return (
      <>
        <Box
          className="new-production-form-section"
          component="form"
          onSubmit={handleSubmit(onSubmit, onInvalid)}
          role="form"
          aria-label={t(
            'productionsAndExports.createNewProduction.documentSelection.headerText'
          )}
        >
          <Box className="new-production-form document-selection">
            <Box className="new-production-form-header">
              <Typography
                id="document-selection-header"
                className="typo-text subhead2_size"
                data-testid={`document-selection-header-text`}
              >
                {t(
                  'productionsAndExports.createNewProduction.documentSelection.headerText'
                )}
              </Typography>
            </Box>
            <Box
              className="new-production-form-body"
              role="group"
              aria-labelledby="document-selection-header"
            >
              {/* Select Documents Section */}
              <Box className="document-selection-section">
                <Box className="document-selection-header">
                  <Typography
                    component="div"
                    className="typo-text subhead4_size"
                    data-testid={`document-selection-select-documents-select-documents-tooltip`}
                  >
                    {t(
                      'productionsAndExports.createNewProduction.documentSelection.selectDocuments'
                    )}
                    <ArrTooltipWrapper
                      closeTooltipOnEscPress
                      trigger={
                        <InfoOutlinedIcon tabIndex={0} className="info-icon" />
                      }
                    >
                      {t(
                        'productionsAndExports.createNewProduction.documentSelection.selectDocumentsTooltip'
                      )}
                    </ArrTooltipWrapper>
                  </Typography>
                  <Typography
                    className="typo-text body2_size doc-count"
                    data-testid={`document-selection-total-docs-label`}
                  >
                    <span
                      className="doc-count-label"
                      data-testid={`document-selection-total-docs-label-text`}
                    >
                      {t(
                        'productionsAndExports.createNewProduction.documentSelection.totalDocsLabel'
                      )}
                    </span>

                    <button
                      type="button"
                      className="doc-count-link"
                      onClick={handleViewDocList}
                      disabled={isDocListLoading || displayedTotalDocs === 0}
                      data-testid="document-view-doclistn"
                    >
                      {displayedTotalDocs}
                    </button>

                    <span
                      className="doc-count-label"
                      data-testid={`document-selection-`}
                    >
                      )
                    </span>
                  </Typography>
                </Box>

                {/* Radio buttons for selection method */}
                <Box className="selection-method-radio">
                  <ArrRadio
                    dataTestId="selectionMethod"
                    name="selectionMethod"
                    value={
                      hasNoSavedDataFromApi
                        ? 'rdbTags'
                        : ENUM_TO_RADIO[selectionMethod] || 'rdbTags'
                    }
                    onChange={handleSelectionMethodChange}
                    data={[
                      {
                        label: t(
                          'productionsAndExports.createNewProduction.documentSelection.selectTags'
                        ),
                        value: 'rdbTags',
                      },
                      {
                        label: t(
                          'productionsAndExports.createNewProduction.documentSelection.selectSearches'
                        ),
                        value: 'rdbSearches',
                      },
                      {
                        label: t(
                          'productionsAndExports.createNewProduction.documentSelection.selectFolders'
                        ),
                        value: 'rdbFolders',
                      },
                    ]}
                    size="medium"
                    disabled={mode === 'view' || isInProgressOrCompleted}
                    uncheckedTabIndex={0}
                    labelTabIndex={-1}
                  />
                </Box>

                {/* Content based on selection method */}
                <Box className="selection-content">
                  <Box className="tags-selection-content">
                    <Box className="tags-selection-header">
                      <Typography
                        className="typo-text subhead4_size"
                        data-testid={`document-selection-typography`}
                      >
                        {selectionMethod === DocumentSelectionMethod.TAGS &&
                          t(
                            'productionsAndExports.createNewProduction.documentSelection.selectTags'
                          )}
                        {selectionMethod === DocumentSelectionMethod.SEARCHES &&
                          t(
                            'productionsAndExports.createNewProduction.documentSelection.selectSearches'
                          )}
                        {selectionMethod === DocumentSelectionMethod.FOLDERS &&
                          t(
                            'productionsAndExports.createNewProduction.documentSelection.selectFolders'
                          )}
                      </Typography>
                      <Box className="tags-action-buttons">
                        <ArrButton
                          variant="secondary"
                          size="medium"
                          onClick={handleClear}
                          disabled={
                            mode === FormMode.VIEW ||
                            !hasAnySelection ||
                            isInProgressOrCompleted
                          }
                          dataTestId="clear-selection-button"
                        >
                          {t(
                            'productionsAndExports.createNewProduction.documentSelection.clear'
                          )}
                        </ArrButton>
                        <ArrButton
                          variant="primary"
                          size="medium"
                          onClick={() => handleUpdateDocCount()}
                          disabled={!isUpdateDocCountEnabled}
                          dataTestId="update-doc-count-button"
                        >
                          {t(
                            'productionsAndExports.createNewProduction.documentSelection.updateDocCount'
                          )}
                        </ArrButton>
                      </Box>
                    </Box>
                    <Box className="tags-tree-container">
                      {isDocumentSelectionLoading ? (
                        <Typography
                          className="typo-text body2_size"
                          data-testid={`document-selection-loading`}
                        >
                          Loading...
                        </Typography>
                      ) : treeData && treeData.length > 0 ? (
                        isLargeData ? (
                          <ArrLazyTreeView
                            key={`ds-lazy-tree-${selectionMethod}-${productionId}`}
                            itemsList={displayedLazyItems}
                            updateTreeData={handleUpdateTreeData}
                            setSelectedItem={handleTreeSelectionChange}
                            checkboxDataTestId="document-selection-lazy-tree-checkbox"
                            treeSelectedItems={
                              Array.isArray(selectedItems) ? selectedItems : []
                            }
                            indeterminateNodeIds={
                              selectionMethod ===
                              DocumentSelectionMethod.SEARCHES
                                ? indeterminateNodeIdsForTree
                                : []
                            }
                            multipleSelection
                            isShowCheckbox
                            isShowCountLabel={false}
                            showActions={false}
                            expandedNodes={expandedNodes}
                            onExpandedNodesChange={(ids) =>
                              setExpandedNodes(ids)
                            }
                            onNodeExpand={async (nodeId: string) => {
                              const srcNode = findNode(
                                fullTreeRef.current,
                                nodeId
                              );
                              if (!srcNode || !Array.isArray(srcNode.children))
                                return [];

                              // Fetch or use existing children
                              const actualChildren = srcNode.children.filter(
                                (c) => c && !c.id.includes('-loading')
                              );

                              const processedChildren = actualChildren.map(
                                (c) => {
                                  const hasKids =
                                    Array.isArray(c.children) &&
                                    c.children.some(
                                      (gc) => gc && !gc.id.includes('-loading')
                                    );
                                  return {
                                    ...c,
                                    hasChildren: hasKids,
                                    isChildrenLoaded: !hasKids,
                                    disabled: isInProgressOrCompleted,
                                  };
                                }
                              );

                              return processedChildren;
                            }}
                          />
                        ) : (
                          <ArrTreeView
                            key={`ds-tree-${selectionMethod}-${productionId}`}
                            itemsList={displayedTreeData}
                            setSelectedItem={handleTreeSelectionChange}
                            treeSelectedItems={
                              Array.isArray(selectedItems) ? selectedItems : []
                            }
                            multipleSelection={true}
                            isShowCheckbox={true}
                            isShowCountLabel={false}
                            defaultExpandedIds={allExpandedIds}
                            isShowFileType={false}
                          />
                        )
                      ) : (
                        <Typography
                          className="typo-text body2_size"
                          data-testid={`document-selection-typography-text`}
                        >
                          {selectionMethod === DocumentSelectionMethod.TAGS &&
                            t(
                              'productionsAndExports.createNewProduction.documentSelection.noTagsAvailable'
                            )}
                          {selectionMethod ===
                            DocumentSelectionMethod.SEARCHES &&
                            t(
                              'productionsAndExports.createNewProduction.documentSelection.searchesPlaceholder'
                            )}
                          {selectionMethod ===
                            DocumentSelectionMethod.FOLDERS &&
                            t(
                              'productionsAndExports.createNewProduction.documentSelection.foldersPlaceholder'
                            )}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Box>
              </Box>

              {/* Include Families Section */}
              <Box className="include-families-section">
                <Box className="include-families-content">
                  <ArrToggle
                    checked={includeFamilies}
                    onChange={handleIncludeFamiliesChange}
                    disabled={mode === 'view' || isInProgressOrCompleted}
                    dataTestId="ProductionDocumentsSelection_ToIncludeFamilies"
                    toggleSize="medium"
                    ariaLabel={t(
                      'productionsAndExports.createNewProduction.documentSelection.includeFamilies'
                    )}
                  />
                  <Box className="include-families-label">
                    <Typography
                      className="typo-text subhead5_size"
                      data-testid={`document-selection-include-families`}
                    >
                      {t(
                        'productionsAndExports.createNewProduction.documentSelection.includeFamilies'
                      )}
                    </Typography>
                    <Typography
                      className="typo-text body2_size family-docs-count doc-count"
                      data-testid={`document-selection-family-docs-label`}
                    >
                      <span
                        className="doc-count-label"
                        data-testid={`document-selection-family-docs-label-text`}
                      >
                        {t(
                          'productionsAndExports.createNewProduction.documentSelection.familyDocsLabel'
                        )}
                      </span>

                      <button
                        type="button"
                        className="doc-count-link"
                        onClick={handleViewFamilyDocList}
                        disabled={isDocListLoading || familyDocs === 0}
                        data-testid="document-selection-button"
                      >
                        {familyDocs}
                      </button>

                      <span
                        className="doc-count-label"
                        data-testid={`document-selection--text`}
                      >
                        )
                      </span>
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
        {/* Export Warning Modal */}
        {showExportWarningModal && (
          <ArrModal
            showModal={showExportWarningModal}
            showClose={false}
            onClose={() => setShowExportWarningModal(false)}
            title={t(
              'productionsAndExports.createNewProduction.documentSelection.exportWarningModal.title'
            )}
            description={t(
              'productionsAndExports.createNewProduction.documentSelection.exportWarningModal.description'
            )}
            dataTestId="documentselection-modal"
            buttons={[
              {
                id: 'ok',
                label: t('common.ok'),
                dataTestId: 'dcm-ok-btn',
                onClick: () => {
                  setShowExportWarningModal(false);

                  // Check if we can skip API call/validation
                  // If isInProgressOrCompleted || !isUpdateNeeded, skip directly to Delivery Options
                  if (isInProgressOrCompleted || !isUpdateNeeded) {
                    // Skip API call and validation, navigate directly to Delivery Options
                    skipToDeliveryOptions?.();
                    return;
                  }

                  // Otherwise, do API call/validation, then skip in callback
                  setValue('action', FormAction.NEXT);
                  handleSubmit((data) => {
                    // Call onSubmit to save - it will handle navigation via mutation callback
                    onSubmit(data);
                  })();
                },
                variant: 'primary',
              },
            ]}
          />
        )}
      </>
    );
  }
);

DocumentSelection.displayName = 'DocumentSelection';

export default DocumentSelection;
